import React, { useEffect, useRef, useState } from 'react'
import type { StyledComponent } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import useScript from './useScript'
import type { Restaurant } from 'shared/types'
import { breakpoints } from 'app/assets/styles'
import { useInitMap } from './useInitMap'
import { useDisplayClusters } from './useDisplayClusters'
import { selectMarker } from 'shared/store/store-locator'
import { displayGeocodedAddressMarker } from './displayMarkers'
import { marketSelect } from '../../../../app/common/utils'
import { markersBase64 } from './markersBase64'

export const CURRENT_MAP_VIEW = {
    STORE_LOCATOR: 'STORE_LOCATOR',
    CLICK_AND_COLLECT: 'CLICK_AND_COLLECT',
    RESTAURANT: 'RESTAURANT'
}
const ALL_FRANCE_ZOOM = window.innerWidth <= breakpoints.medium ? 5 : 6
const ALL_REUNION_ZOOM = window.innerWidth <= breakpoints.medium ? 9 : 10

export const ALL_ZOOM = marketSelect({
    reunion: ALL_REUNION_ZOOM,
    france: ALL_FRANCE_ZOOM
})

export const DETAILED_ZOOM = 13
const FRANCE_BBOX = [-5.08255, 41.99299, 10.166473, 51.992333]
const REUNION_BBOX = [
    55.2164265141,
    -21.3897305009,
    55.8366923779,
    -20.8717409099
]

const BBOX = marketSelect({
    reunion: REUNION_BBOX,
    france: FRANCE_BBOX
})

const FRANCE_CENTER = {
    lat: 46.6305581,
    lng: 1.8505104
}

const REUNION_CENTER = {
    lat: -21.114533,
    lng: 55.5320624999
}

export const CENTER = marketSelect({
    france: FRANCE_CENTER,
    reunion: REUNION_CENTER
})

const woosmapPublicKey = process.env.REACT_APP_WOOSMAP_PUBLIC_KEY || ''
const woosmapUrlWithoutKey =
    process.env.REACT_APP_WOOSMAP_MAP_URL_WITHOUT_KEY || ''

type MapWoosmapProps = {
    onSelectRestaurant: () => void,
    onDeselectRestaurant?: () => void,
    ContainerMap: StyledComponent<>,
    restaurant?: Restaurant,
    currentView: string
}

let geocodedAddressMarker

export const MapWoosmap = ({
    onSelectRestaurant,
    onDeselectRestaurant,
    ContainerMap,
    restaurant,
    currentView = CURRENT_MAP_VIEW.STORE_LOCATOR
}: MapWoosmapProps) => {
    const mapContainerRef = useRef()
    const dispatch = useDispatch()

    const { activeRestaurant, geocodedAddress } = useSelector(
        (state: RootState) => state.storeLocatorPageState
    )

    const [map, setMap] = useState(null)
    const [zoomAndBbox, setZoomAndBbox] = useState({
        zoom: ALL_FRANCE_ZOOM,
        bbox: BBOX
    })

    const isMapLoaded = useScript(`${woosmapUrlWithoutKey}${woosmapPublicKey}`)

    const onStoreSelected = (store: any) => {
        try {
            const lat = store.lat || store.geometry.coordinates[1]
            const lng = store.lng || store.geometry.coordinates[0]
            map.setCenter({ lat, lng })
            map.setZoom(DETAILED_ZOOM)
            if (currentView !== CURRENT_MAP_VIEW.RESTAURANT) {
                !!onSelectRestaurant && onSelectRestaurant()
                dispatch(selectMarker(store.id || store.properties.store_id))
            }
        } catch (e) {
            console.error(e)
        }
    }

    useInitMap({
        currentView,
        isMapLoaded,
        mapContainerRef,
        restaurant,
        onDeselectRestaurant,
        onStoreSelected,
        setMap,
        setZoomAndBbox
    })

    useDisplayClusters({
        currentView,
        isMapLoaded,
        map,
        zoomAndBbox,
        onStoreSelected
    })

    useEffect(() => {
        if (
            map &&
            currentView !== CURRENT_MAP_VIEW.RESTAURANT &&
            activeRestaurant
        ) {
            onStoreSelected(activeRestaurant)
        }
    }, [activeRestaurant, currentView])

    useEffect(() => {
        if (
            map &&
            currentView !== CURRENT_MAP_VIEW.RESTAURANT &&
            geocodedAddress
        ) {
            if (!!geocodedAddressMarker) {
                geocodedAddressMarker.setMap(null)
            }
            geocodedAddressMarker = new window.woosmap.map.Marker({
                position: geocodedAddress,
                icon: {
                    url: markersBase64.markerPin
                }
            })
            geocodedAddressMarker.setMap(map)
            map.setCenter(geocodedAddress)
            map.setZoom(DETAILED_ZOOM)
        }
    }, [geocodedAddress, currentView])

    return <ContainerMap id={'map'} ref={mapContainerRef}></ContainerMap>
}
